import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable } from 'rxjs';

import {
    HisBackendService,
    HisUserProfileService,
    HisAuthService,
    GenericValue,
    DynamicObjects
} from 'hisng';

import { Publication } from './../model/publication';
import { DynamicFormGroupUtils } from '../../common/service/dynamic-content/dynamic-form-group-utils';

@Injectable()
export class PublicationService {

    public showList = false;
    constructor( private backendService: HisBackendService,
        public userProfileService: HisUserProfileService,
        private auth: HisAuthService,
        private route: ActivatedRoute,
        private router: Router,
        private dynamicFormGroupUtils: DynamicFormGroupUtils ) { }

    /**
     * get publication by Id from database
     *
     * @param id publication ID
     */
    getPublicationById( id: number ): Observable<Publication> {
        return this.backendService.getData( `api/v1/fs/res/publication/${id}` );
    }

    /**
     * get publication with details (includes all collections and citations available)
     *
     * @param id - id of the publication
     */
    getPublicationWithDetailsById( id: number ): Observable<Publication> {
        return this.backendService.getData( `api/v1/fs/res/publication/${id}/details?citeFormat=html` );
    }

    /**
     * get publications by ids from database
     *
     * @param ids a set of publication ids
     */
    getPublicationsByIds( ids: number[] ): Observable<Publication[]> {
        let idsStr = '';
        if ( ids.length > 0 ) {
            for ( let id of ids ) {
                idsStr += id + ',';
            }
            idsStr = idsStr.substring( 0, idsStr.length - 1 );
        }
        return this.backendService.getData( `api/v1/fs/res/publications/${idsStr}` );
    }

    /**
     * get publications by ids from database.
     * Next to publications one is allwoed to see by view rights, this methods also delivers
     * import candidates imported by the current user.
     *
     * @param ids a set of publication ids
     */
    getPublicationsIncludingOwnImportsByIds( ids: number[] ): Observable<Publication[]> {
        let idsStr = '';
        if ( ids.length > 0 ) {
            for ( let id of ids ) {
                idsStr += id + ',';
            }
            idsStr = idsStr.substring( 0, idsStr.length - 1 );
        }
        return this.backendService.getData( `api/v1/fs/res/publications/${idsStr}/includingOwnImports` );
    }

    /**
     * save publication to database
     *
     * @param publication to save
     * @param child
     * @param removeConferenceName default=true
     */
    savePublication( publication: Publication, child?: Publication, removeConferenceName = true ): Observable<Publication> {
        if (removeConferenceName) {
            delete publication.conferenceName;
        }
        if ( publication.id ) {
            return this.backendService.putData( `api/v1/fs/res/publication/${publication.id}`, publication);
        } else {
            const childParam = child ? `?child=${child.id}` : '';
            return this.backendService.postData( 'api/v1/fs/res/publication' + childParam, publication );
        }
    }

    /**
     * save publication to database
     *
     * @param publication to save
     * @param removeConferenceName default=true
     */
    savePublicationWithCahangeRequest( publication: Publication, removeConferenceName = true ): Observable<Publication> {
        return this.backendService.putData( `api/v1/fs/res/publication/${publication.id}/changerequest`, publication);
    }

    /**
     * delete publication from database
     *
     * @param id of publication to delete
     * @return 0: failure, 1: success
     */
    deletePublication( id: number ): Observable<number> {
        return this.backendService.delete( `api/v1/fs/res/publication/${id}` );
    }

    /**
     * Check if the current user has the right to delete the publication with the given id.
     * This is granted for own import candidates or if the publication passes the rightsparameter check.
     *
     * @param publicationId Id of publication
     */
    hasRightToDeletePublication( publicationId: number ): Observable<boolean> {
        return this.backendService.getData( `api/v1/fs/res/publication/hasRightToDelete/${publicationId}` );
    }

    /**
     * check if publication can be deleted and has not further references to other objects.
     *
     * @param publicationId Id of publication
     */
    canDeletePublication( publicationId: number ): Observable<string> {
        return this.backendService.getData( `api/v1/fs/res/canDeletePublication/${publicationId}` );
    }

    getPublicationStatus(): Observable<GenericValue[]> {
        return this.backendService.getData( 'api/v1/fs/res/state/publication' );
    }

    /**
     * Fetch publications by criteria from the database.
     *
     * @param searchText Text contained in publication.
     * @param publicationTypeIds Array of publication type ids.
     * @param from Start date.
     * @param until End date.
     * @param limit Limit the number of results.
     * @param offset Offset for pagination.
     */
    getPublicationsByCriteria( searchText?: string,
        publicationTypeIds?: number[],
        from?: string,
        until?: string,
        limit?: number,
        offset?: number  ): Observable<Publication[]> {
        if ( publicationTypeIds?.length > 0 ) {
            return this.backendService.getData( `api/v1/fs/res/publication?q=${searchText}&k=${publicationTypeIds}` );
        } else if(from || until) {
            let isLimit = limit !== null && limit !== undefined ? '&l=' + limit : '';
            let isOffset = offset ? '&offset=' + offset : '';
            let isUntil = until ? '&until=' + until : '';
            let isFrom = from ? '&from=' + from : '';
            let isSearchText = searchText ? 'q='+ searchText : '';
            let isPublicationTypeId = publicationTypeIds ? '&k='+ publicationTypeIds : '';
            let urlQueryTemp = 'api/v1/fs/res/publication?' + isSearchText + isPublicationTypeId + isFrom + isUntil + isLimit + isOffset;
            let urlQuery =  urlQueryTemp.replace('?&', '?');
            return this.backendService.getData(urlQuery);
        } else {
            return this.backendService.getData( `api/v1/fs/res/publication?q=${searchText}` ); // &l="105"
        }
    }

    /**
     * return bibtex file for a publication
     *
     * @param publicationId publication id
     */
    getBibTex( publicationId: number ): Observable<string> {
        return this.backendService.getData( `api/v1/fs/res/publication/${publicationId}/bibtex` );
    }

    canEditPublication( id: number ): Observable<boolean> {
        return this.backendService.getData( `api/v1/fs/res/publication/canEdit/${id}` );
    }

    canRequestChange( id: number ): Observable<boolean> {
        return this.backendService.getData( `api/v1/fs/res/publication/canRequestChange/${id}` );
    }

    canViewPublication( id: number ): Observable<boolean> {
        return this.backendService.postData(
            `api/v1/fs/res/hasRightsOnObject/Publication/${id}/true`,
            [
                'RIGHT_FS_RES_PUBLICATION_VIEW_PUBLICATION',
                'RIGHT_FS_RES_PUBLICATION_VIEW_PUBLICATION_OWN',
                'RIGHT_FS_RES_PUBLICATION_VIEW_PUBLICATION_BY_TYPE',
                'RIGHT_FS_RES_PUBLICATION_VIEW_PUBLICATION_VISIBLE'
            ] );
    }

    checkIsDirty(publication: Publication): Observable<boolean> {
        return new Observable((observer: any) => {
            if (!publication || !publication.id) {
                observer.next(true);
                observer.complete();
            } else {
                this.getPublicationById(publication.id).subscribe((savedPublication: Publication) => {
                    this.dynamicFormGroupUtils.setDynamicComponentObject(publication);

                    /* eslint-disable no-underscore-dangle */
                    observer.next(
                        JSON.stringify({
                            ...publication, lockVersion: null, _dynamicComponent: null, parent: {id: publication.parent?.id}
                        }) !==
                        JSON.stringify({
                            ...savedPublication, lockVersion: null, _dynamicComponent: null, parent: {id: savedPublication.parent?.id}
                        }) ||
                        publication._dynamicComponent?.dynamicObjects != null &&
                        savedPublication._dynamicComponent?.dynamicObjects != null &&
                        !this.compareValuesFromDynComponents(
                            publication._dynamicComponent.dynamicObjects, savedPublication._dynamicComponent.dynamicObjects
                        )
                    );
                    /* eslint-enable no-underscore-dangle */
                    observer.complete();
                });
            }
        });
    }

    /**
     * service must not be called without "Login"
     *
     * @param view
     * @param isShowList if true then we want to show the list view of the search result
     */
    switchListTable(view: string, isShowList: boolean) {
        if (null===isShowList) {
            this.showList = !this.showList;
        } else {
            this.showList = isShowList;
        }
        if ( this.auth.hasAny( 'RIGHT_IS_LOGGED_IN' ) ) {
            let searchResultMode = this.showList ? 'Liste' : 'Tabelle';
            this.userProfileService.setNewValue(view, searchResultMode, 'Tabelle' ).subscribe();
            this.router.navigate( [], {
                relativeTo: this.route,
                queryParams: { [view]: searchResultMode },
                queryParamsHandling: 'merge',
                replaceUrl: true
            } ).then( navigationSuccess => {
                if (navigationSuccess === false) {
                    console.error( 'navigation failed, navigationSuccess =', navigationSuccess );
                }
            } );
            console.log( 'Set ' + view + ' =', searchResultMode );
        }
    }

    checkDuplicatesOnCreate( attributesMap: Record<string, string[]> ): Observable<Publication[]> {
        let request = { entityConfigUniquename: 'new-publication', entityAttributes: attributesMap };
        console.log( 'checkDuplicatesOnCreate: request =', JSON.stringify(request) );
        // #287505 Durch { blockUi: false } schalten wir den Klickvorhang ab, der sonst auf der Bearbeiten-Seite kurz aufflackert
        return this.backendService.postData( 'api/v1/fs/res/publication/searchDuplicates/onCreate', request, { blockUI: false } );
    }

    navigateToCreateParentPublication(showSuccessMessage: boolean) {
        let showBackButton = false;
        this.route.queryParams.subscribe( ( queryParam: Params ) => {
            showBackButton = queryParam.showBackButton ? queryParam.showBackButton === 'true' : false;
        });
        const queryparams = showSuccessMessage ? { fromForm: true, showMsg: showSuccessMessage, showBackButton }
            : { fromForm: true, showBackButton };
        this.router.navigateByUrl( '/', { skipLocationChange: true } ).then( () =>
            this.router.navigate( ['/pub/create/'], { queryParams: queryparams } )
        );
    }

    navigateToEditParentPublication(showSuccessMessage: boolean, parentId: number) {
        if ( parentId ) {
            let showBackButton = false;
            this.route.queryParams.subscribe( ( queryParam: Params ) => {
                showBackButton = queryParam.showBackButton ? queryParam.showBackButton === 'true' : false;
            } );
            const queryparams = showSuccessMessage ? { fromForm: true, showMsg: showSuccessMessage, showBackButton }
                : { fromForm: true, showBackButton };
            this.router.navigateByUrl( '/', { skipLocationChange: true } ).then( () =>
                this.router.navigate( ['/pub/edit/' + parentId], { queryParams: queryparams } )
            );
        }
    }

    private compareValuesFromDynComponents(dynObjects1: DynamicObjects[], dynObjects2: DynamicObjects[]): boolean {
        return dynObjects1.every(dynObj => {
            const dynObj2 = dynObjects2.find(d2 => dynObj.dynamicClassName === d2.dynamicClassName);
            const equal = dynObj.values.every(val => {
                const val2 = dynObj2.values.find(v2 => val.attributeName === v2.attributeName);
                return (val.value == null && val2.value == null) || val.value === val2.value;
            });

            return dynObj.children && dynObj2.children ?
                equal && this.compareValuesFromDynComponents(dynObj.children, dynObj2.children) : equal;
        });
    }

}
